// Copyright (c) 2022 by Move Closer

import { Component } from 'vue-property-decorator'

import { CartData, CartIdentifier } from '../../../../contexts'
import { Inject } from '../../../../support'

import { AuthMixin, IAuthMixin } from '../../../auth/shared'

import { Cart } from '../../models/cart'
import { CartModel, CartMutationTypes } from '../../contracts'
import { CART_ID_KEY, CartServiceType, ICartService } from '../../services/cart'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface IBaseCart extends IAuthMixin {
  /**
   * Instance of cart service.
   */
  cartService: ICartService

  /**
   * Initializes cart for user/guest.
   */
  cartId: CartIdentifier

  /**
   * Returns if cart is loading.
   */
  cartLoading: boolean

  /**
   * Determines the model of set cart.
   */
  cart: CartModel | null

  /**
   * Create empty cart.
   */
  createCart: () => Promise<void>

  /**
   * Create customer cart.
   */
  createCustomerCart: () => Promise<void>

  /**
   * Deletes the cart
   */
  deleteCart: () => void

  /**
   * Load existing cart.
   */
  loadCart: (cartId: string) => Promise<void>

  /**
   * Refreshes the cart
   */
  refreshCart: (cart: CartModel) => void
}

/**
 * Base cart mixin that every pseudo mixin based in cart **SHOULD** extend.
 *
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
@Component
export class BaseCartMixin extends AuthMixin implements IBaseCart {
  @Inject(CartServiceType)
  public readonly cartService!: ICartService

  /**
   * @inheritDoc
   */
  public get cart (): CartModel | null {
    const cartFromStore: CartData | null = this.$store.getters['checkout/cart']
    return cartFromStore !== null ? Cart.hydrate<CartModel>(cartFromStore) : null
  }

  /**
   * @inheritDoc
   */
  public get cartId (): CartIdentifier {
    return this.$store.getters['checkout/cartId']
  }

  /**
   * @inheritDoc
   */
  public get cartLoading (): boolean {
    return this.$store.getters['checkout/loading']
  }

  /**
   * @inheritDoc
   */
  public async createCart (): Promise<void> {
    const guestCart = await this.cartService.resolveEmptyCart()

    this.refreshCart(guestCart)

    this.cartService.setCartId(guestCart.id)
  }

  /**
   * @inheritDoc
   */
  public async createCustomerCart (): Promise<void> {
    const customerCart = await this.cartService.loadCustomerCart()

    this.refreshCart(customerCart)

    this.cartService.setCartId(customerCart.id)
  }

  /**
   * @inheritDoc
   */
  public deleteCart (): void {
    this.cartService.deleteCartId()

    this.$store.commit(CartMutationTypes.SetCartId, null)
    this.$store.commit(CartMutationTypes.SetCart, null)
  }

  /**
   * @inheritDoc
   */
  public async loadCart (cartId: string): Promise<void> {
    const existingGuestCart = await this.cartService.resolveCart(cartId, 5)

    if (cartId !== existingGuestCart.id) {
      this.cartService.setCartId(existingGuestCart.id)
    }

    this.refreshCart(existingGuestCart)
  }

  /**
   * @inheritDoc
   */
  public refreshCart (cart: CartModel): void {
    this.$store.commit(CartMutationTypes.SetCartId, cart.id)
    this.$store.commit(CartMutationTypes.SetCart, cart)
  }
}
